import React from 'react';
import { Descriptions, Table, Tabs, Tag, Empty, Space, Image } from 'antd';
import EllipsisCell from '../../../../components/common/EllipsisCell';
import { EnvironmentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import OrderStatusFlow from '../../../../components/common/OrderStatusFlow';
import {
  ORDER_STATUS_OPTIONS,
  CHANNEL_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  SHORTAGE_REASON_OPTIONS,
} from '../../../../constants/order';

/**
 * 订单详情共享内容组件
 * 同时被订单列表 Drawer 和独立详情页路由复用
 *
 * @param {Object} props
 * @param {Object} props.orderDetail - { order, pickingDetails, substituteRecords }
 */
const OrderDetailContent = ({ orderDetail }) => {
  const { t } = useTranslation();

  if (!orderDetail) return null;

  // 商品缩略图占位 fallback（与 master-data/item/list 一致）
  const IMAGE_FALLBACK =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==';

  // 统一的商品缩略图渲染（48x48、cover、圆角、点击预览、无图 fallback）
  const renderItemImage = (url) => {
    if (!url) return '-';
    return (
      <Image
        src={url}
        width={48}
        height={48}
        style={{ objectFit: 'cover', borderRadius: 4 }}
        fallback={IMAGE_FALLBACK}
      />
    );
  };

  const order = orderDetail.order || {};
  const orderItems = order.items || [];
  const pickingDetails = orderDetail.pickingDetails || [];
  const substituteRecords = orderDetail.substituteRecords || [];

  // 计算当前订单状态节点索引
  const getCurrentStatusIndex = () => {
    if (order.outboundCompleteTime) return 4;
    if (order.pickingEndTime) return 3;
    if (order.pickingStartTime) return 2;
    if (order.readyToPickTime) return 1;

    switch (order.orderStatus) {
      case 'CREATED':
        return 0;
      case 'IN_PROGRESS':
        return 2;
      case 'COMPLETED':
        return 4;
      case 'RETURN':
        return 4;
      default:
        return 0;
    }
  };

  // 构建状态数据映射
  const buildStatusData = () => {
    const data = {};
    const currentIndex = getCurrentStatusIndex();
    const nodes = [
      { key: 'ordersAccepted', time: order.createTime },
      { key: 'readyToPick', time: order.readyToPickTime },
      { key: 'startPicking', time: order.pickingStartTime },
      { key: 'pickingCompleted', time: order.pickingEndTime },
      { key: 'outboundCompleted', time: order.outboundCompleteTime },
    ];
    nodes.forEach((node, index) => {
      const isCompleted = index < currentIndex;
      const hasTime = !!node.time;
      if (isCompleted || hasTime) {
        data[node.key] = { time: node.time || null, completed: isCompleted };
      }
    });
    return data;
  };

  // 解析 deliveryAddress JSON
  const parseDelivery = () => {
    if (!order.deliveryAddress) return null;
    try {
      const parsed = typeof order.deliveryAddress === 'string'
        ? JSON.parse(order.deliveryAddress)
        : order.deliveryAddress;
      // deliveryAddress 可能嵌套在 value 属性下
      return parsed?.value || parsed;
    } catch (e) {
      return { addressLine1: order.deliveryAddress };
    }
  };
  const delivery = parseDelivery();

  // 商品明细列
  const itemColumns = [
    { title: t('order.order.detail.items.lineNo'), dataIndex: 'lineNo', width: 80, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.items.itemCode'), dataIndex: 'itemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.items.originalItemCode'), dataIndex: 'originalItemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v || '-'}</EllipsisCell> },
    { title: t('order.order.detail.items.itemName'), dataIndex: 'itemName', width: 200, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.items.image'), dataIndex: 'imageUrl', width: 80, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: renderItemImage },
    { title: t('order.order.detail.items.planQty'), dataIndex: 'planQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.items.actualPickedQty'), dataIndex: 'actualPickedQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v ?? '-'}</EllipsisCell> },
    { title: t('order.order.detail.items.unitPrice'), dataIndex: 'unitPrice', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{`฿${v?.toFixed(2) || '0.00'}`}</EllipsisCell> },
    { title: t('order.order.detail.items.finalLineAmount'), dataIndex: 'paidPrice', width: 120, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v != null ? `฿${v.toFixed(2)}` : '-'}</EllipsisCell> },
    { title: t('order.order.detail.items.lineAmount'), dataIndex: 'lineAmount', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{`฿${v?.toFixed(2) || '0.00'}`}</EllipsisCell> },
    {
      title: t('order.order.detail.items.isFreebie'),
      dataIndex: 'isFreebie',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    { title: t('order.order.detail.items.promotion'), dataIndex: 'promotionType', width: 120, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v || '-'}</EllipsisCell> },
    { title: t('order.order.detail.items.returnQty'), dataIndex: 'returnQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v ?? '-'}</EllipsisCell> },
    { title: t('order.order.detail.items.remark'), dataIndex: 'remark', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v || '-'}</EllipsisCell> },
  ];

  // 拣选明细列
  const pickingColumns = [
    { title: t('order.order.detail.picking.taskNo'), dataIndex: 'taskNo', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.picking.itemCode'), dataIndex: 'itemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.picking.itemName'), dataIndex: 'itemName', width: 200, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.picking.image'), dataIndex: 'imageUrl', width: 80, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: renderItemImage },
    { title: t('order.order.detail.picking.planQty'), dataIndex: 'planQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.picking.actualPickedQty'), dataIndex: 'actualQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    {
      title: t('order.order.detail.picking.isCancelled'),
      dataIndex: 'isCancelled',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'default' : 'green'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.isRepicked'),
      dataIndex: 'isRepicked',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'blue' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.isSubstituted'),
      dataIndex: 'isSubstituted',
      width: 130,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'orange' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.isConfirmShort'),
      dataIndex: 'isConfirmShort',
      width: 150,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'red' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.shortageReason'),
      dataIndex: 'shortageReason',
      width: 120,
      ellipsis: { showTitle: false },
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (reason) => {
        if (!reason) return <EllipsisCell>{'-'}</EllipsisCell>;
        const option = SHORTAGE_REASON_OPTIONS.find((opt) => opt.value === reason);
        const displayText = t(option?.label || reason);
        return <EllipsisCell>{displayText}</EllipsisCell>;
      },
    },
    { title: t('order.order.detail.picking.pickerName'), dataIndex: 'pickerName', width: 120, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.picking.pickTime'), dataIndex: 'pickTime', width: 180, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v ? dayjs(v).format('DD-MM-YYYY HH:mm:ss') : '-'}</EllipsisCell> },
  ];

  // 替换明细列
  const substituteColumns = [
    { title: t('order.order.detail.substitute.originalItemCode'), dataIndex: 'originalItemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.originalItemName'), dataIndex: 'originalItemName', width: 200, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.originalImage'), dataIndex: 'originalImageUrl', width: 80, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: renderItemImage },
    { title: t('order.order.detail.substitute.originalQty'), dataIndex: 'originalQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.originalPrice'), dataIndex: 'originalPrice', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{`฿${v?.toFixed(2) || '0.00'}`}</EllipsisCell> },
    { title: t('order.order.detail.substitute.substituteItemCode'), dataIndex: 'substituteItemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.substituteItemName'), dataIndex: 'substituteItemName', width: 200, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.substituteImage'), dataIndex: 'substituteImageUrl', width: 80, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: renderItemImage },
    { title: t('order.order.detail.substitute.substituteQty'), dataIndex: 'substituteQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.substitutePrice'), dataIndex: 'substitutePrice', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{`฿${v?.toFixed(2) || '0.00'}`}</EllipsisCell> },
    {
      title: t('order.order.detail.substitute.priceDiff'),
      dataIndex: 'priceDiff',
      width: 100,
      align: 'right',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (diff) => (
        <span style={{ color: diff > 0 ? '#ff4d4f' : diff < 0 ? '#52c41a' : '#000' }}>
          <EllipsisCell>฿{diff?.toFixed(2) || '0.00'}</EllipsisCell>
        </span>
      ),
    },
    { title: t('order.order.detail.substitute.pickerName'), dataIndex: 'pickerName', width: 120, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.substituteTime'), dataIndex: 'substituteTime', width: 180, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v ? dayjs(v).format('DD-MM-YYYY HH:mm:ss') : '-'}</EllipsisCell> },
  ];

  return (
    <>
      {/* 订单状态流程 */}
      <OrderStatusFlow
        current={getCurrentStatusIndex()}
        statusData={buildStatusData()}
        isCancelled={order.orderStatus === 'CANCELLED'}
        cancelTime={order.cancelTime}
        isReturned={order.orderStatus === 'RETURN'}
        returnTime={order.returnTime}
      />

      {/* 基本信息 */}
      <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('order.order.detail.basic.orderNo')}>
          {order.orderNo || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.upstreamOrderNo')}>
          {order.upstreamOrderNo || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.orderType')}>
          {order.orderType || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.channel')}>
          {(() => {
            const option = CHANNEL_OPTIONS.find((opt) => opt.value === order.channel);
            return option ? t(option.label) : (order.channel || '-');
          })()}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.orderStatus')}>
          <Tag color={ORDER_STATUS_OPTIONS.find((opt) => opt.value === order.orderStatus)?.color}>
            {t(ORDER_STATUS_OPTIONS.find((opt) => opt.value === order.orderStatus)?.label || order.orderStatus)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.paymentMethod')}>
          {PAYMENT_METHOD_OPTIONS.find((opt) => opt.value === order.paymentMethod)?.label || order.paymentMethod}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.storeCode')}>
          {order.storeCode || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.skuQty')}>
          {order.skuQty || 0}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.totalMu')}>
          {order.totalMu || 0}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.totalAmount')}>
          ฿{order.subTotal?.toFixed(2) || '0.00'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.grandTotal')}>
          {order.grandTotal != null ? `฿${order.grandTotal.toFixed(2)}` : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.cancelTime')} span={2}>
          {order.cancelTime ? dayjs(order.cancelTime).format('DD-MM-YYYY HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.createTime')} span={2}>
          {order.createTime ? dayjs(order.createTime).format('DD-MM-YYYY HH:mm:ss') : '-'}
        </Descriptions.Item>
      </Descriptions>

      {/* Delivery Info */}
      <Descriptions
        bordered
        column={4}
        size="small"
        title={t('order.order.detail.delivery.title')}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label={t('order.order.detail.delivery.recipientName')}>
          {delivery
            ? [delivery.firstName, delivery.lastName].filter(Boolean).join(' ') || order.buyerName || '-'
            : order.buyerName || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.phoneNumber')}>
          {delivery?.mobileNo || order.buyerPhone || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.note')}>
          {delivery?.deliveryNote || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.address')}>
          {delivery?.addressLine1 || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.subDistrict')}>
          {delivery?.subDistrict || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.district')}>
          {delivery?.city || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.province')}>
          {delivery?.state || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.country')}>
          {delivery?.country || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.postcode')}>
          {delivery?.postalCode || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.latLng')} span={3}>
          {delivery?.latitude && delivery?.longitude ? (
            <a
              href={`https://www.google.com/maps?q=${delivery.latitude},${delivery.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#13c2c2' }}
            >
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {delivery.latitude}, {delivery.longitude}
            </a>
          ) : '-'}
        </Descriptions.Item>
      </Descriptions>

      {/* 三个 Tab */}
      <Tabs
        defaultActiveKey="1"
        type="card"
        items={[
          {
            key: '1',
            label: t('order.order.detail.tabs.orderDetails'),
            children: (
              <Table
                dataSource={orderItems}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 1200 }}
                locale={{ emptyText: <Empty description={t('common.message.noData')} /> }}
                columns={itemColumns}
              />
            ),
          },
          {
            key: '2',
            label: t('order.order.detail.tabs.pickingDetails'),
            children: (
              <div>
                <Table
                  dataSource={pickingDetails}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  scroll={{ x: 1660 }}
                  locale={{ emptyText: <Empty description={t('order.order.detail.picking.emptyText')} /> }}
                  columns={pickingColumns}
                />
                {pickingDetails.length > 0 && (
                  <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                    <Space>
                      <span>
                        {t('order.order.detail.picking.summaryPicked')}:{' '}
                        {pickingDetails.filter((item) => !item.isCancelled && item.actualQty > 0).length}
                      </span>
                      <span>|</span>
                      <span>
                        {t('order.order.detail.picking.summaryTotal')}: {pickingDetails.length}
                      </span>
                      <span>|</span>
                      <span>
                        {t('order.order.detail.picking.summaryRate')}:{' '}
                        {(
                          (pickingDetails.filter((item) => !item.isCancelled && item.actualQty > 0).length /
                            pickingDetails.length) *
                          100
                        ).toFixed(1)}
                        %
                      </span>
                    </Space>
                  </div>
                )}
              </div>
            ),
          },
          {
            key: '3',
            label: t('order.order.detail.tabs.substituteDetail'),
            children: (
              <Table
                dataSource={substituteRecords}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 1600 }}
                locale={{ emptyText: <Empty description={t('common.message.noData')} /> }}
                columns={substituteColumns}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default OrderDetailContent;
